import { useRef, useState, useCallback } from 'react';
import type { FC, MouseEvent, KeyboardEvent, FocusEvent } from 'react';
import { Transforms } from 'slate';
import type { Element } from 'slate';
import { ReactEditor, useSlate } from 'slate-react';
import styled, { css } from 'styled-components';

import {
  TouchDialog,
  Popover,
  Icon,
  registerIcon,
  useOuterEvent,
  isTouchDevice,
  Menu,
  Button,
  defaultThemeProp,
  useI18n,
  Tooltip,
  calculateFontSize
} from '@pega/cosmos-react-core';
import * as arrowMicroDownIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/arrow-micro-down.icon';

import EditorCommands from '../utils/EditorCommands';
import { useRTEContext } from '../RichTextEditor.context';

import { getKeyCommand } from './utils';

registerIcon(arrowMicroDownIcon);

const StyledTextSelect = styled(Button)(({ theme }) => {
  const fontSizes = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);
  return css`
    color: ${theme.base.palette['foreground-color']};
    display: inline-flex;
    align-items: center;
    border-radius: calc(0.25 * ${theme.base['border-radius']});
    height: calc(4 * ${theme.base.spacing});
    padding: calc(0.5 * ${theme.base.spacing});
    font-size: ${fontSizes.xxs};

    span {
      white-space: nowrap;
    }

    :focus {
      box-shadow: ${theme.base.shadow.focus};
    }
  `;
});

StyledTextSelect.defaultProps = defaultThemeProp;

const StyledSelectMenu = styled.div`
  min-width: 10rem;
`;

const getTextCommand = (num: number, osx: boolean) => {
  return getKeyCommand(osx, ({ ctrl, alt }) => `${ctrl}${alt}${num}`);
};

const TextSelect: FC<{ osx: boolean }> = ({ osx }) => {
  const t = useI18n();
  const { disabled } = useRTEContext();
  const [open, setOpen] = useState(false);
  const selecting = useRef(false);
  const openedByClick = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef(null);
  const menuRef = useRef(null);
  const touchDialogRef = useRef<HTMLDivElement>(null);
  const editor = useSlate();

  const textFormats = useCallback((): {
    text: string;
    subText: string;
    type: Element['type'];
  }[] => {
    return [
      {
        text: t('rte_heading_style_normal'),
        subText: getTextCommand(0, osx),
        type: 'paragraph'
      },
      {
        text: t('rte_heading_style_heading_1'),
        subText: getTextCommand(1, osx),
        type: 'heading-1'
      },
      {
        text: t('rte_heading_style_heading_2'),
        subText: getTextCommand(2, osx),
        type: 'heading-2'
      },
      {
        text: t('rte_heading_style_heading_3'),
        subText: getTextCommand(3, osx),
        type: 'heading-3'
      },
      {
        text: t('rte_heading_style_heading_4'),
        subText: getTextCommand(4, osx),
        type: 'heading-4'
      }
    ];
  }, [osx])();

  const textMapping: { [key: string]: string } = {
    paragraph: t('rte_heading_style_normal'),
    'heading-1': t('rte_heading_style_heading_1'),
    'heading-2': t('rte_heading_style_heading_2'),
    'heading-3': t('rte_heading_style_heading_3'),
    'heading-4': t('rte_heading_style_heading_4'),
    '': t('rte_heading_style_normal')
  };

  useOuterEvent('mousedown', [buttonRef, popoverRef, menuRef, touchDialogRef], () => {
    setOpen(false);
  });

  const onTextSelect = (type: string, e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const format = textFormats.find(item => item.type === type);
    if (format) {
      EditorCommands.setBlock(format.type, editor);
    }
    if (!openedByClick.current) {
      buttonRef.current?.focus();
    }
    setOpen(false);
    selecting.current = true;
    setTimeout(() => {
      if (!ReactEditor.isFocused(editor) && openedByClick.current) {
        const selection = editor.selection;
        ReactEditor.focus(editor);
        if (selection) Transforms.select(editor, selection);
      }
    }, 0);
  };

  const menuContent = (
    <Menu
      items={textFormats.map(({ text, type, subText }) => ({
        id: type,
        primary: text,
        secondary: [subText],
        selected: type === EditorCommands.getActiveBlockType(editor)
      }))}
      focusControlEl={isTouchDevice ? undefined : buttonRef.current || undefined}
      mode='single-select'
      ref={menuRef}
      onItemClick={onTextSelect}
    />
  );

  return (
    <>
      <StyledTextSelect
        variant='simple'
        type='button'
        disabled={disabled}
        icon={false}
        ref={buttonRef}
        onMouseDown={(e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
          openedByClick.current = true;
        }}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Enter' && !selecting.current) {
            e.preventDefault();
            setOpen(true);
            openedByClick.current = false;
          } else if (e.key === 'Enter') {
            e.preventDefault();
            selecting.current = false;
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            setOpen(false);
          }
        }}
        onFocus={(e: FocusEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e: MouseEvent) => e.stopPropagation()}
        aria-expanded={open}
        aria-label={`Text formatting toolbar. ${t('rte_heading_style')}. ${
          textMapping[EditorCommands.getActiveBlockType(editor)]
        } selected`}
        aria-haspopup
      >
        <span>{textMapping[EditorCommands.getActiveBlockType(editor)]}</span>
        <Icon name='arrow-micro-down' />
      </StyledTextSelect>
      {buttonRef.current && (
        <Tooltip target={buttonRef.current} showDelay='none' hideDelay='none'>
          {t('rte_heading_style')}
        </Tooltip>
      )}
      {isTouchDevice ? (
        <TouchDialog
          ref={touchDialogRef}
          open={open}
          heading={t('rte_heading_style')}
          onDismiss={() => setOpen(false)}
        >
          {menuContent}
        </TouchDialog>
      ) : (
        <Popover
          show={open}
          as={StyledSelectMenu}
          target={buttonRef.current}
          placement='bottom-start'
          ref={popoverRef}
        >
          {menuContent}
        </Popover>
      )}
    </>
  );
};

export default TextSelect;
